import React, { useEffect, useState } from 'react';
import { Spinner, SpinnerSize, mergeStyleSets } from '@fluentui/react';
import { X } from '@phosphor-icons/react';
import { boroondaraPalette } from '../../theme/boroondaraTheme';
import { accessService } from '../../services/accessService';

const p = boroondaraPalette;

interface SA2DetailPanelProps {
  sa2Id: string;
  sa2Name: string;
  year: number;
  onClose: () => void;
}

interface CensusKPI {
  label: string;
  value: string;
  icon: string;
  color: string;
}

const styles = mergeStyleSets({
  panel: {
    padding: 24,
    borderRadius: 4,
    background: '#FFFFFF',
    border: `1px solid ${p.border}`,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    marginBottom: 24,
    '@media (max-width: 768px)': { padding: 16 },
  } as any,
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  name: { fontSize: 18, fontWeight: 700, color: p.text },
  badge: {
    fontSize: 11,
    fontWeight: 600,
    color: p.primary,
    background: p.selectedBg,
    borderRadius: 999,
    padding: '2px 10px',
    marginTop: 4,
    display: 'inline-block',
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 8,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    selectors: { ':hover': { background: p.bg2 } },
  } as any,
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 12,
  } as any,
  kpiCard: {
    padding: 14,
    borderRadius: 4,
    background: p.bg2,
    border: `1px solid ${p.borderSoft}`,
  },
  kpiLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: p.text3,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: 4,
  } as any,
  kpiValue: { fontSize: 20, fontWeight: 700, color: p.text },
  centered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 100,
  },
});

export default function SA2DetailPanel({ sa2Id, sa2Name, year, onClose }: SA2DetailPanelProps) {
  const [kpis, setKpis] = useState<CensusKPI[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    // Fetch multiple topics in parallel for the KPI summary
    Promise.all([
      accessService.get(`/spatial/choropleth?topic=demographics&metric=population_total&year=${year}`),
      accessService.get(`/spatial/choropleth?topic=demographics&metric=median_age&year=${year}`),
      accessService.get(`/spatial/choropleth?topic=housing&metric=median_rent_weekly&year=${year}`),
      accessService.get(`/spatial/choropleth?topic=income&metric=median_household_weekly&year=${year}`),
      accessService.get(`/spatial/choropleth?topic=employment&metric=unemployment_rate&year=${year}`),
      accessService.get(`/spatial/choropleth?topic=housing&metric=total_dwellings&year=${year}`),
    ])
      .then(([popRes, ageRes, rentRes, incRes, empRes, dwellRes]) => {
        if (cancelled) return;

        const findVal = (res: any) => {
          const feat = res.data?.features?.find((f: any) => f.properties?.sa2_id === sa2Id);
          return feat?.properties?.metric_value;
        };

        const pop = findVal(popRes);
        const age = findVal(ageRes);
        const rent = findVal(rentRes);
        const inc = findVal(incRes);
        const unemp = findVal(empRes);
        const dwell = findVal(dwellRes);

        setKpis([
          { label: 'Population', value: pop != null ? Number(pop).toLocaleString() : 'N/A', icon: 'People', color: '#0078D4' },
          { label: 'Median Age', value: age != null ? String(age) : 'N/A', icon: 'ContactInfo', color: '#7C7CFF' },
          { label: 'Median Weekly Rent', value: rent != null ? `$${Number(rent).toLocaleString()}` : 'N/A', icon: 'Home', color: '#34D399' },
          { label: 'Median HH Income', value: inc != null ? `$${Number(inc).toLocaleString()}/wk` : 'N/A', icon: 'Money', color: '#FBBF24' },
          { label: 'Unemployment', value: unemp != null ? `${unemp}%` : 'N/A', icon: 'WorkforceManagement', color: '#F87171' },
          { label: 'Dwellings', value: dwell != null ? Number(dwell).toLocaleString() : 'N/A', icon: 'CityNext2', color: '#38BDF8' },
        ]);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sa2Id, year]);

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div>
          <div className={styles.name}>{sa2Name}</div>
          <div className={styles.badge}>ABS Census {year}</div>
        </div>
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
          <X size={12} weight="bold" style={{ color: p.text3 }} />
        </button>
      </div>

      {loading ? (
        <div className={styles.centered}>
          <Spinner size={SpinnerSize.medium} label="Loading census data..." />
        </div>
      ) : (
        <div className={styles.kpiGrid}>
          {kpis.map((k) => (
            <div key={k.label} className={styles.kpiCard}>
              <div className={styles.kpiLabel}>{k.label}</div>
              <div className={styles.kpiValue}>{k.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
